import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { useRetrieveProjectToolsConfiguration } from "@planzone/view-models";
import {
  usePlanzonePermissions,
  useProjectPermissions,
} from "@components/permissions";
import { DocumentsIcon } from "./documentsIcon";
import { MembersIcon } from "./membersIcon";
import { DiscussionsIcon } from "./discussionsIcon";
import { BudgetIcon } from "./budgetIcon";
import { ReportsExportsIcon } from "./reportsExportsIcon";
import { ExternalLinksList } from "./externalLinksList";
import { Props } from "./types";
import styles from "./styles.scss";

function SideBar({ project }: Props) {
  const { t } = useTranslation();
  const { isAdministrator } = usePlanzonePermissions();
  const { isProjectManager } = useProjectPermissions(project);
  const { config } = useRetrieveProjectToolsConfiguration(project);

  return (
    <div className={styles.sideBar}>
      <ul className={styles.navigation}>
        <li className={styles.item}>
          <NavLink
            to={`/projects/${project.uuid}/members`}
            className={({ isActive }) =>
              classNames(styles.link, { [styles.selectedLink]: isActive })
            }
          >
            <MembersIcon className={styles.icon} />
            <p className={styles.name}>{t("projectPage.navigation.members")}</p>
          </NavLink>
        </li>
        {!project.isTemplate && config.areDiscussionsEnabled && (
          <li className={styles.item}>
            <NavLink
              to={`/projects/${project.uuid}/discussions`}
              className={({ isActive }) =>
                classNames(styles.link, { [styles.selectedLink]: isActive })
              }
            >
              <DiscussionsIcon className={styles.icon} />
              <p className={styles.name}>
                {t("projectPage.navigation.discussions")}
              </p>
            </NavLink>
          </li>
        )}
        {config.areDocumentsEnabled && (
          <li className={styles.item}>
            <NavLink
              to={`/projects/${project.uuid}/documents`}
              className={({ isActive }) =>
                classNames(styles.link, { [styles.selectedLink]: isActive })
              }
            >
              <DocumentsIcon className={styles.icon} />
              <p className={styles.name}>
                {t("projectPage.navigation.reports")}
              </p>
            </NavLink>
          </li>
        )}
        {(isAdministrator || isProjectManager) && config.isBudgetEnabled && (
          <li className={styles.item}>
            <NavLink
              to={`/projects/${project.uuid}/budget`}
              className={({ isActive }) =>
                classNames(styles.link, { [styles.selectedLink]: isActive })
              }
            >
              <BudgetIcon className={styles.icon} />
              <p className={styles.name}>
                {t("projectPage.navigation.budget")}
              </p>
            </NavLink>
          </li>
        )}
        {(isAdministrator || isProjectManager) && config.areReportsEnabled && (
          <li className={styles.item}>
            <NavLink
              to={`/projects/${project.uuid}/reports`}
              className={({ isActive }) =>
                classNames(styles.link, { [styles.selectedLink]: isActive })
              }
            >
              <ReportsExportsIcon className={styles.icon} />
              <p className={styles.name}>
                {t("projectPage.navigation.reportsExports")}
              </p>
            </NavLink>
          </li>
        )}
      </ul>
      <div className={styles.externalLinksList}>
        <ExternalLinksList project={project} />
      </div>
    </div>
  );
}

export { SideBar };
